<script setup lang="ts">
import { useDefaultStore } from "@/store";
import arrowDown from "@/assets/image/icons/arrow-down.svg";

const store = useDefaultStore();
const route = useRoute();
const dynamicLineStyle = ref({});
const tabsWrapper = ref<HTMLElement | null>(null);
const isServicesOpen = ref(false);

function toggleServices() {
  isServicesOpen.value = !isServicesOpen.value;
}

const setActiveModernLine = () => {
  const activeEl1 = tabsWrapper.value?.querySelector(".active") as HTMLElement | null;
  if (activeEl1) {
    const unit = "px";
    dynamicLineStyle.value = {
      width: `${activeEl1.offsetWidth}${unit}`,
      left: `${activeEl1.offsetLeft}${unit}`,
    };
  } else {
    dynamicLineStyle.value = {};
  }
};

onMounted(() => {
  setTimeout(() => {
    setActiveModernLine();
  }, 500);
});

watch(
  () => route.path,
  () => {
    nextTick(() => {
      setActiveModernLine();
    });
    if (isServicesOpen.value) {
      toggleServices();
    }
  }
);

watch(
  () => store.isMobile,
  () => {
    nextTick(() => {
      setActiveModernLine();
    });
    if (isServicesOpen.value) {
      toggleServices();
    }
  }
);

async function onClickedMenuItem() {
  store.isMobileToggled = false;
}

watch(
  () => store.langSelected,
  (newLang) => {
    if (newLang) {
      setTimeout(() => {
        setActiveModernLine();
      }, 100);
      if (isServicesOpen.value) {
        toggleServices();
      }
    }
  }
);

watch(
  () => store.isMobileToggled,
  (newVal) => {
    document.body.style.overflow = newVal ? "hidden" : "";
  }
);
</script>

<template>
  <header class="h-header md:h-header-lg flex w-full items-center justify-center bg-transparent">
    <div id="inner-wrapper" class="flex w-full max-w-screen-xl items-center justify-between px-[16px]">
      <router-link :to="{ name: 'HomePage' }" aria-label="Return to Home Page">
        <img v-if="store.isMobile" src="@/assets/image/icm-logotip.svg" alt="logo" />
        <img v-else src="@/assets/image/icm-logo.svg" alt="logo" />
      </router-link>
      <div v-if="!store.isMobile">
        <div ref="tabsWrapper" class="relative flex gap-10">
          <div
            class="absolute bottom-0 left-0 h-[3px] w-[96.77px] transition-all duration-300 ease-in-out"
            :style="dynamicLineStyle"
            :class="route.name === 'NotFound' ? 'bg-stransparent' : 'bg-primary'"
          ></div>
          <router-link :to="{ name: 'HomePage' }">
            <div
              :class="{ active: route.name === 'HomePage' }"
              class="text-text-light text-xl font-medium uppercase transition-opacity duration-200 hover:opacity-70"
            >
              {{ $t("app.nav.home") }}
            </div>
          </router-link>
          <div
            class="relative"
            :class="{
              active: store.services.map((s) => s.name).includes(route.name as string),
            }"
          >
            <button class="flex cursor-pointer items-center gap-2" @click="toggleServices">
              <div
                class="text-text-light text-xl font-medium uppercase transition-opacity duration-200 hover:opacity-70"
                :class="{ 'text-primary': store.services.map((s) => s.name).includes(route.name as string) }"
              >
                {{ $t("app.nav.services.label") }}
              </div>

              <img
                :src="arrowDown"
                alt="Toggle services"
                class="transition-transform duration-300"
                :class="{ 'rotate-180': isServicesOpen }"
              />
            </button>

            <transition name="fade">
              <div
                v-if="isServicesOpen"
                class="bg-text-dark absolute top-full left-0 z-50 mt-2 rounded-md border-2 border-[#494E5B] p-3 shadow-lg"
              >
                <router-link
                  v-for="service in store.services"
                  :key="service.name"
                  :to="{ name: service.name }"
                  :class="[
                    'hover:text-text-dark block px-4 py-2 text-xl uppercase transition-colors duration-200 hover:bg-gray-100',
                    route.name === service.name ? 'text-primary' : 'text-text-light',
                  ]"
                  @click="isServicesOpen = false"
                >
                  {{ service.label }}
                </router-link>
              </div>
            </transition>
          </div>

          <router-link :to="{ name: 'ContactPage' }">
            <div
              :class="{ active: route.name === 'ContactPage' }"
              class="text-text-light text-xl font-medium uppercase transition-opacity duration-200 hover:opacity-70"
            >
              {{ $t("app.nav.contact") }}
            </div>
          </router-link>
        </div>
      </div>
      <div v-if="!store.isMobile">
        <lang-switcher
          :selected="store.langSelected"
          :only-toggle="false"
          :can-change-router="null"
          :show-caret="true"
          @change="store.langSelected = $event"
        ></lang-switcher>
      </div>
      <div v-if="store.isMobile">
        <burger-menu-btn
          style="position: relative"
          :is-mobile-header-toggled="store.isMobileToggled"
          @toggle-burger="store.isMobileToggled = !store.isMobileToggled"
        ></burger-menu-btn>
        <transition name="slide-fade">
          <header-mobile
            v-if="store.isMobileToggled"
            @click-menu-item="onClickedMenuItem"
            @close="store.isMobileToggled = false"
          ></header-mobile>
        </transition>
      </div>
    </div>
  </header>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
